/**
 * Two apps behind one router.
 *
 * `/admin/*` is staff-only and sits behind a Supabase session. `/pay/:token`
 * is the customer's, reached from an email, with no account and no Supabase
 * session at all — its only credential is the token in the URL plus the
 * verification cookie the server sets. Keeping them in separate directories
 * makes it hard to accidentally import an admin data hook into a page a
 * customer can reach.
 */

import { Routes, Route, Navigate } from 'react-router-dom';

import { AdminApp } from './admin/AdminApp.js';
import { PayApp } from './pay/PayApp.js';

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/admin" replace />} />

      <Route path="/admin/*" element={<AdminApp />} />

      <Route path="/pay/:token" element={<PayApp />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function NotFound() {
  return (
    <Shell>
      <h1 className="text-xl font-semibold text-slate-900">Page not found</h1>
      <p className="mt-2 text-slate-600">
        If you followed a payment link from an email, it may have expired. Reply to that email and
        we will send a new one.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <span className="text-lg font-bold tracking-tight text-gold-700">Gold Star Printing</span>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">{children}</main>
    </div>
  );
}
